import React, { useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import PropTypes from "prop-types";
import { hideModal } from "../../../actions/actionCreators";
import MaterialModal from "../MaterialModal";
import withModal from "../withModal";
import FormControl from "@material-ui/core/FormControl";
import FormControlLabel from "@material-ui/core/FormControlLabel";
import RadioGroup from "@material-ui/core/RadioGroup";
import Radio from "@material-ui/core/Radio";
import TextField from "@material-ui/core/TextField";
import h from "../../../helpers";

const DownloadAdminFieldsModal = ({ isOpen, modalProps }) => {
    const dispatch = useDispatch();

    // State/Props
    const [dataType, setDataType] = useState("");
    const [owner, setOwner] = useState("Current");
    const roles = useSelector(state => state.session.roles || []);
    const experianDownload = roles.includes("Experian Admin") || roles.includes("Super Admin");
    const [simpleRespond, setSimpleRespond] = useState(false);
    const [isEmailsValid, setIsEmailsValid] = useState(true);
    const [contactEmail, setContactEmail] = useState("");
    const [error, setError] = useState(false);

    function isValidateEmailsFunc(string) {
        if (string == undefined || !string.trim()) {
            return false;
        }

        const regex = /^([\w-\.]+@([\w-]+\.)+[\w-]{2,4})?$/;
        const result = string.replace(/\s/g, "").split(/,|;/);
        for (let i = 0; i < result.length; i++) {
            if (!regex.test(result[i])) {
                return false;
            }
        }

        return true;
    }

    const handleSettingValue = val => {
        setIsEmailsValid(isValidateEmailsFunc(val));
        setContactEmail(val);
    };

    const onSecondaryButtonRequest = () => {
        if (simpleRespond) {
            if (isValidateEmailsFunc(contactEmail)) {
                setIsEmailsValid(true);
                const saveUrl =
                    "/Tree/EmailExperianAssetsMetadata?" +
                    h.serialize({
                        emailRecipient: contactEmail,
                    });

                return fetch(saveUrl, { method: "POST", credentials: "same-origin" })
                    .then(h.checkStatus)
                    .then(h.toJson)
                    .then(() => {
                        setIsEmailsValid(false);
                        setTimeout(() => {
                            setIsEmailsValid(true);
                        }, 3000);
                        hide();
                    })
                    .catch(error => {
                        h.error("Error sending Experian Assets metadata to email", error);
                        setError(true);
                    });
            } else {
                setIsEmailsValid(false);
                h.error("Type in valid email!!!", error);
                setError(true);
            }
        }
    };

    const hide = () => {
        setDataType("");
        setSimpleRespond(false);
        setIsEmailsValid(true);
        setContactEmail("");
        dispatch(hideModal("DOWNLOAD_FIELDS"));
    };

    const onDownloadClick = () => {
        const isExperianAsset = owner !== "Current";
        if (dataType == "fields") {
            hide();
            h.throwGTMTrigger("button_click", "button", "Edit Field", "ActionsMenu", "Download File for field");
            const excelLink = `/Tree/GetFixedLayoutInstructionFileAsync?experian=${isExperianAsset}`;
            window.location = excelLink;
        } else if (dataType == "metadata") {
            hide();
            h.throwGTMTrigger("button_click", "button", "Edit Field", "ActionsMenu", "Download File for metadata");
            const excelLink = `/Tree/GetFieldMetataFileAsync?experian=${isExperianAsset}`;
            window.location = excelLink;
        }
    };

    return (
        <MaterialModal
            id="user-details-modal"
            isOpen={isOpen}
            onClose={hide}
            title="Download"
            showPrimaryButton={true}
            primaryButtonText="Download"
            isPrimaryButtonDisabled={dataType == "" || modalProps.disabled}
            onPrimaryButtonRequest={onDownloadClick}
            onSecondaryButtonRequest={onSecondaryButtonRequest}
            secondaryButtonText={"Email"}
            isSecondaryButtonDisabled={simpleRespond ? !isEmailsValid : true}
            showSecondaryButton={simpleRespond ? true : false}
        >
            <div>
                Select which type of data you want to download.
                <FormControl>
                    <RadioGroup
                        row
                        name="type"
                        value={dataType}
                        onChange={e => {
                            setDataType(e.target.value);
                            if (e.target.value === "metadata" && owner === "Experian" && !simpleRespond) {
                                setSimpleRespond(true);
                            } else {
                                setSimpleRespond(false);
                            }
                        }}
                    >
                        <div style={{ marginLeft: "25px", marginTop: "10px" }}>
                            <FormControlLabel value="fields" control={<Radio color="primary" />} label="Fields" />
                            <FormControlLabel value="metadata" control={<Radio color="primary" />} label="Metadata" />
                        </div>
                    </RadioGroup>
                </FormControl>
            </div>
            {experianDownload && (
                <div>
                    Select who owns the data you are downloading.
                    <FormControl>
                        <RadioGroup
                            row
                            name="owner"
                            value={owner}
                            onChange={e => {
                                setOwner(e.target.value);
                                if (e.target.value === "Experian" && dataType === "metadata" && !simpleRespond) {
                                    setSimpleRespond(true);
                                } else {
                                    setSimpleRespond(false);
                                }
                            }}
                        >
                            <div style={{ marginLeft: "25px", marginTop: "10px", marginBottom: "10px" }}>
                                <FormControlLabel
                                    value="Current"
                                    control={<Radio color="primary" />}
                                    label="Current Company"
                                />
                                <FormControlLabel
                                    value="Experian"
                                    control={<Radio color="primary" />}
                                    label="Experian Asset"
                                />
                            </div>
                        </RadioGroup>
                    </FormControl>
                </div>
            )}
            {simpleRespond && (
                <div>
                    Set email address to receive the Experian assets metadata.
                    <FormControl>
                        <TextField
                            id="flow-control-custom-emails"
                            error={!isEmailsValid}
                            label="Emails"
                            type="text"
                            onChange={e => handleSettingValue(e.target.value)}
                            value={contactEmail}
                            helperText={
                                `${!isEmailsValid ? "Email(s) are invalid. " : ""}` +
                                "We will send email(s) with attachment. You can add multiple emails seperated by a semicolon."
                            }
                            style={{ width: "90%", marginLeft: "20px" }}
                        />
                    </FormControl>
                </div>
            )}
        </MaterialModal>
    );
};

DownloadAdminFieldsModal.propTypes = {
    isOpen: PropTypes.bool,
    hideModal: PropTypes.func,
    modalProps: PropTypes.any,
    roles: PropTypes.array,
};

export default withModal(DownloadAdminFieldsModal, "DOWNLOAD_FIELDS");
